
import SEO from '../components/SEO';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Lock, CheckCircle, Clock, Award, TrendingUp, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuthStore } from '../store/authStore';

interface Course {
  id: string; title: string; slug: string; shortDescription: string; requiredTier: string;
  category: string; difficulty: string; thumbnailUrl?: string; duration: number;
  lessonCount: number; order: number; enrolled?: boolean; progress?: number;
}

const FALLBACK_COURSES: any[] = [
  { id: 1, title: 'Crypto Staking 101', category: 'Beginner', duration: '15 min', lessonsCount: 5, tier: 'free', level: 'beginner', difficulty: 'beginner', description: 'Learn the fundamentals of crypto staking, how it works, and why it generates passive income.', totalLessons: 5 },
  { id: 2, title: 'Understanding APY vs APR', category: 'Beginner', duration: '10 min', lessonsCount: 3, tier: 'free', level: 'beginner', difficulty: 'beginner', description: 'Understand the difference between APY and APR and why it matters for your staking returns.', totalLessons: 3 },
  { id: 3, title: 'Liquid Staking Deep Dive', category: 'Intermediate', duration: '20 min', lessonsCount: 6, tier: 'free', level: 'intermediate', difficulty: 'intermediate', description: 'Explore liquid staking protocols like Lido and Rocket Pool, and how liquid staking tokens work.', totalLessons: 6 },
  { id: 4, title: 'ATOM & Cosmos Staking Guide', category: 'Intermediate', duration: '18 min', lessonsCount: 5, tier: 'free', level: 'intermediate', difficulty: 'intermediate', description: 'A complete guide to staking ATOM on the Cosmos Hub, choosing validators, and maximizing rewards.', totalLessons: 5 },
  { id: 5, title: 'DeFi Yield Strategies', category: 'Advanced', duration: '25 min', lessonsCount: 7, tier: 'pro', level: 'advanced', difficulty: 'advanced', description: 'Advanced DeFi yield strategies including liquidity provision, yield farming, and risk management.', totalLessons: 7 },
  { id: 6, title: 'Building a Staking Portfolio', category: 'Advanced', duration: '30 min', lessonsCount: 8, tier: 'pro', level: 'advanced', difficulty: 'advanced', description: 'Build a diversified staking portfolio that balances risk, liquidity, and returns.', totalLessons: 8 },
];

export default function EducationPage() {
  const { user } = useAuthStore();

  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      const response = await api.get('/education/courses');
      return response.data.data?.courses || response.data.data || [];
    },
    retry: 2,
  });

  const { data: progressData } = useQuery({
    queryKey: ['education-progress'],
    queryFn: async () => {
      const response = await api.get('/education/progress');
      return response.data.data?.progress || null;
    },
    enabled: !!user,
    retry: 1,
  });

  const apiCourses = Array.isArray(coursesData) ? coursesData : [];
  const courses: Course[] = apiCourses.length > 0 ? apiCourses : FALLBACK_COURSES;
  const progress = progressData;

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'pro': return 'bg-steel-blue text-white';
      case 'premium': return 'bg-gradient-cta text-white';
      case 'enterprise': return 'bg-cream text-navy-dark';
      default: return 'bg-sky-blue/20 text-sky-blue';
    }
  };

  const canAccessCourse = (course: Course) => {
    if (!user) return (course.requiredTier || (course as any).tier) === 'free';
    const tierLevels: Record<string, number> = { free: 0, pro: 1, premium: 2, enterprise: 3 };
    const userLevel = tierLevels[user.subscriptionTier] ?? 0;
    const courseLevel = tierLevels[course.requiredTier || (course as any).tier] ?? 0;
    return userLevel >= courseLevel;
  };

  return (
    <div className="min-h-screen bg-navy-dark py-12">
      <SEO title="Crypto Staking Education Hub" description="Learn how to earn passive income through crypto staking. Free courses covering DeFi basics, staking strategies, yield optimization, and wealth building with cryptocurrency." canonical="https://www.residualvault.com/education" keywords="crypto staking education, learn DeFi, staking for beginners, passive income crypto, DeFi courses, crypto yield farming guide" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8"><h1 className="text-4xl font-bold mb-2"><span className="text-cream">Education </span><span className="gradient-text-neon">Hub</span></h1><p className="text-sky-blue">Learn the principles of passive income and wealth building</p></div>
        {user && progress && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium mb-8">
            <div className="grid md:grid-cols-4 gap-6">
              <div><div className="flex items-center mb-2"><Award className="w-5 h-5 mr-2 text-sky-blue" /><span className="text-sky-blue">Level</span></div><div className="text-4xl font-bold gradient-text-neon">{progress.level || 1}</div></div>
              <div><div className="flex items-center mb-2"><Zap className="w-5 h-5 mr-2 text-sky-blue" /><span className="text-sky-blue">Experience</span></div><div className="text-4xl font-bold gradient-text-neon">{progress.experiencePoints || 0} XP</div></div>
              <div><div className="flex items-center mb-2"><TrendingUp className="w-5 h-5 mr-2 text-sky-blue" /><span className="text-sky-blue">Streak</span></div><div className="text-4xl font-bold gradient-text-neon">{progress.currentStreak || 0} days</div></div>
              <div><div className="flex items-center mb-2"><CheckCircle className="w-5 h-5 mr-2 text-sky-blue" /><span className="text-sky-blue">Completed</span></div><div className="text-4xl font-bold gradient-text-neon">{progress.totalCoursesCompleted || 0}</div></div>
            </div>
          </motion.div>
        )}
        {coursesLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(5)].map((_, i) => (<div key={i} className="card-premium h-96 animate-pulse" />))}</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course, index) => {
              const tier = course.requiredTier || (course as any).tier || 'free';
              const description = course.shortDescription || (course as any).description || '';
              const lessons = course.lessonCount || (course as any).totalLessons || 0;
              const hasAccess = canAccessCourse(course);
              const isEnrolled = course.enrolled || false;
              const completionPercent = course.progress || 0;
              return (
                <motion.div key={course.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className={`card-premium group ${!hasAccess ? 'opacity-75' : ''}`}>
                  <div className="h-48 bg-gradient-accent flex items-center justify-center relative rounded-t-xl">
                    <BookOpen className="w-16 h-16 text-white/30" />
                    <div className={`absolute top-4 right-4 ${getTierBadge(tier)} text-xs font-bold px-3 py-1 rounded-full`}>{tier.toUpperCase()}</div>
                    {!hasAccess && (<div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-t-xl"><Lock className="w-12 h-12 text-white" /></div>)}
                    {isEnrolled && completionPercent > 0 && (<div className="absolute bottom-0 left-0 right-0 h-2 bg-sky-blue/30"><div className="h-full bg-neon-red transition-all duration-500" style={{ width: `${completionPercent}%` }} /></div>)}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-cream mb-2 group-hover:text-neon-red transition-colors">{course.title}</h3>
                    <p className="text-sky-blue text-sm mb-4 line-clamp-2">{description}</p>
                    <div className="flex items-center justify-between text-sm text-sky-blue mb-4">
                      <div className="flex items-center"><Clock className="w-4 h-4 mr-1" />{course.duration || 0} min</div>
                      <div className="flex items-center"><BookOpen className="w-4 h-4 mr-1" />{lessons} lessons</div>
                      <span className={`px-2 py-1 rounded text-xs ${course.difficulty === 'beginner' ? 'bg-blue-900/10 text-blue-900 border border-blue-900/30' : course.difficulty === 'intermediate' ? 'bg-blue-900/10 text-blue-900 border border-blue-900/30' : 'bg-neon-red/10 text-neon-red border border-neon-red/30'}`}>{course.difficulty || 'beginner'}</span>
                    </div>
                    {!hasAccess ? (
                      <Link to="/pricing" className="block w-full text-center py-3 px-4 bg-steel-blue/20 border border-sky-blue/30 text-sky-blue rounded-lg font-medium hover:bg-steel-blue/30 transition">🔒 Upgrade to {tier}</Link>
                    ) : (
                      <Link to={`/education/courses/${course.id}`} className="block w-full text-center btn-neon">{completionPercent > 0 && completionPercent < 100 ? 'Continue Learning' : completionPercent === 100 ? 'Review Course' : 'Start Course'}</Link>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
        {user?.subscriptionTier === 'free' && courses.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-12 card-premium text-center">
            <h3 className="text-2xl font-bold text-cream mb-4">Unlock Advanced Courses</h3>
            <p className="text-sky-blue mb-6 max-w-2xl mx-auto">Upgrade to Pro, Premium, or Enterprise to access advanced DeFi strategies, wealth building techniques, and institutional management courses.</p>
            <Link to="/pricing" className="btn-neon inline-block">View Pricing</Link>
          </motion.div>
        )}
      </div>
    </div>
  );
}
